import {
  TenantConfig,
  TenantESignConfig,
  TenantRegionalConfig,
} from '../config/tenant-config';

/**
 * WI-F7 — runtime TenantConfig store (ADO #38535).
 *
 * Minimal, additive singleton. WI-F1 fetches the runtime {@link TenantConfig}
 * at boot and WI-F3/F4 feed it into MSAL, but nothing kept it around for the
 * rest of the app. The provider-agnostic e-sign flow needs `TenantConfig.esign`
 * at signing time (long after boot), so this holds the resolved config.
 *
 * It is set ONCE at boot in `main.tsx`, right after `fetchTenantConfig`
 * succeeds and before the app renders. Everything else only READS it. Mirrors
 * the existing `MsalInstanceManager` / `Environment` singleton pattern.
 */
class TenantConfigManager {
  private static instance: TenantConfigManager | null = null;
  private config: TenantConfig | null = null;

  private constructor() {}

  public static getInstance(): TenantConfigManager {
    if (this.instance === null) {
      this.instance = new TenantConfigManager();
    }
    return this.instance;
  }

  /** Set once at boot after the runtime config resolves. */
  public setConfig(config: TenantConfig): void {
    this.config = config;
  }

  /** The full resolved tenant config, or `null` before boot completes. */
  public getConfig(): TenantConfig | null {
    return this.config;
  }

  /**
   * The tenant's e-sign config, or `null` if config is not yet resolved. Note
   * `esign.provider` itself may be `null` (tenant has not onboarded e-sign) —
   * callers must handle that distinct case.
   */
  public getESignConfig(): TenantESignConfig | null {
    return this.config?.esign ?? null;
  }

  /** The tenant's regional formatting, or `null` if config is not yet resolved. */
  public getRegionalConfig(): TenantRegionalConfig | null {
    return this.config?.regional ?? null;
  }

  /** Test-only: drop the singleton + stored config between cases. */
  public static reset(): void {
    this.instance = null;
  }
}

export default TenantConfigManager;
